import React, { useEffect, useState } from 'react';
import { Moon, MoonStar } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';

const label = (qh, active) => {
  if (!qh.enabled) return 'Quiet hours';
  return active ? `Quiet until ${qh.end}` : `Quiet ${qh.start}–${qh.end}`;
};

export const QuietHoursControl = ({ quietHours, quietActive, onSave }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(quietHours);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setDraft(quietHours); }, [quietHours, open]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(draft.enabled ? `Quiet hours on, ${draft.start} to ${draft.end}` : 'Quiet hours off');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save quiet hours');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`pill-toggle ${quietActive ? 'quiet' : quietHours.enabled ? 'on' : 'off'}`}
          title="Quiet hours: no alerts or sounds on any of your devices during this window"
          data-testid="quiet-hours-toggle"
        >
          {quietActive ? <MoonStar size={15} /> : <Moon size={15} />}
          {label(quietHours, quietActive)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="quiet-popover" data-testid="quiet-hours-popover">
        <div className="quiet-row">
          <div>
            <div className="quiet-title">Quiet hours</div>
            <div className="quiet-sub">No lock-screen alerts, sounds or radio playback on any of your devices. Messages still arrive and count as unread.</div>
          </div>
          <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} data-testid="quiet-hours-switch" />
        </div>
        <div className="quiet-times">
          <label>
            From
            <input type="time" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} disabled={!draft.enabled} data-testid="quiet-hours-start" />
          </label>
          <label>
            To
            <input type="time" value={draft.end} onChange={(e) => setDraft({ ...draft, end: e.target.value })} disabled={!draft.enabled} data-testid="quiet-hours-end" />
          </label>
        </div>
        <div className="quiet-footer">
          <span className="quiet-sub">Every day · times in this device's time zone</span>
          <button type="button" className="alerts-enable" onClick={save} disabled={saving} data-testid="quiet-hours-save">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
